import {
  type Address,
  type ChainId,
  getChainAddresses,
  Market,
  type MarketParams
} from '@morpho-org/blue-sdk'
import { blueAbi } from '@morpho-org/blue-sdk-viem'
import { decodeFunctionResult, encodeFunctionData, size } from 'viem'

import { type SimulatedCall, toSimulationTx, type Transaction } from '@/services/simulate/simulate'

/**
 * Refinance-only, two-market variant of {@link buildMarketPositionReadTxs}. A refinance moves a
 * position from a SOURCE market to a TARGET market in one bundle, so the recap needs the
 * post-action state of BOTH markets. We append four `view` staticcalls to the bundle tail, in a
 * fixed order:
 *
 *   [ position(source), market(source), position(target), market(target) ]
 *
 * so in the simulation result they occupy the last four `calls`:
 *   source position `calls.at(-4)`, source market `calls.at(-3)`,
 *   target position `calls.at(-2)`, target market `calls.at(-1)`.
 *
 * The SDK only ever *prepends* authorization txs, never appends, so this tail alignment holds
 * regardless of how many approvals the bundle carries. Both reads are `view` (no logs, no value
 * moved) and share the connected sender as `from`, as `eth_simulateV1` requires.
 */
export const REFINANCE_POSITION_READ_CALL_COUNT = 4

/** Exact ABI-encoded byte sizes of the reads' returnData (see marketPositionReads.ts). */
const POSITION_RETURN_DATA_SIZE = 3 * 32
const MARKET_RETURN_DATA_SIZE = 6 * 32

/**
 * Builds the `[position(source), market(source), position(target), market(target)]` read txs for
 * the refinance bundle's tail. Returns `undefined` on a chain blue-sdk doesn't know
 * (`getChainAddresses` throws), so the caller falls back to the JS-math estimate.
 */
export const buildRefinancePositionReadTxs = ({
  chainId,
  sourceMarketParams,
  targetMarketParams,
  user
}: {
  chainId: ChainId
  sourceMarketParams: MarketParams
  targetMarketParams: MarketParams
  user: Address
}): Transaction[] | undefined => {
  let morpho: Address
  try {
    morpho = getChainAddresses(chainId).blue
  } catch {
    return undefined
  }

  const positionData = (id: `0x${string}`) =>
    encodeFunctionData({ abi: blueAbi, functionName: 'position', args: [id, user] })
  const marketData = (id: `0x${string}`) =>
    encodeFunctionData({ abi: blueAbi, functionName: 'market', args: [id] })

  return [
    toSimulationTx(user, { to: morpho, value: 0n, data: positionData(sourceMarketParams.id) }),
    toSimulationTx(user, { to: morpho, value: 0n, data: marketData(sourceMarketParams.id) }),
    toSimulationTx(user, { to: morpho, value: 0n, data: positionData(targetMarketParams.id) }),
    toSimulationTx(user, { to: morpho, value: 0n, data: marketData(targetMarketParams.id) })
  ]
}

const decodePositionMarketPair = ({
  positionCall,
  marketCall,
  marketParams
}: {
  positionCall: SimulatedCall | undefined
  marketCall: SimulatedCall | undefined
  marketParams: MarketParams
}): { collateral: bigint; borrowAssets: bigint } | undefined => {
  if (!positionCall || !marketCall) return undefined
  if (positionCall.status === false || marketCall.status === false) return undefined
  if (
    size(positionCall.returnData) !== POSITION_RETURN_DATA_SIZE ||
    size(marketCall.returnData) !== MARKET_RETURN_DATA_SIZE
  )
    return undefined

  try {
    const [, borrowShares, collateral] = decodeFunctionResult({
      abi: blueAbi,
      functionName: 'position',
      data: positionCall.returnData
    })
    const [
      totalSupplyAssets,
      totalSupplyShares,
      totalBorrowAssets,
      totalBorrowShares,
      lastUpdate,
      fee
    ] = decodeFunctionResult({
      abi: blueAbi,
      functionName: 'market',
      data: marketCall.returnData
    })

    const market = new Market({
      params: marketParams,
      totalSupplyAssets,
      totalSupplyShares,
      totalBorrowAssets,
      totalBorrowShares,
      lastUpdate,
      fee
    })

    return {
      collateral,
      // Rounds up by default (= the protocol's `toAssetsUp` for debt).
      borrowAssets: market.toBorrowAssets(borrowShares)
    }
  } catch {
    return undefined
  }
}

/**
 * Decodes the post-action collateral + debt for BOTH the source and target markets from the
 * refinance bundle's trailing four reads (the result of {@link buildRefinancePositionReadTxs}).
 * Returns `undefined` (→ caller shows the JS-math estimate) when the reads are absent or malformed:
 * a short `calls` array, fewer calls than the bundle we posted (a relay that dropped entries), a
 * reverted read, a mis-sized returnData, or a decode failure — the same fail-safe rules as the
 * single-market {@link decodeMarketPositionReads}.
 */
export const decodeRefinancePositionReads = ({
  calls,
  expectedCallCount,
  sourceMarketParams,
  targetMarketParams
}: {
  calls: readonly SimulatedCall[] | undefined
  expectedCallCount: number
  sourceMarketParams: MarketParams
  targetMarketParams: MarketParams
}):
  | {
      source: { collateral: bigint; borrowAssets: bigint }
      target: { collateral: bigint; borrowAssets: bigint }
    }
  | undefined => {
  if (!calls || calls.length < REFINANCE_POSITION_READ_CALL_COUNT) return undefined
  // The route relays one entry per simulated tx and only ever PREPENDS, so fewer entries than we
  // posted means the relay dropped calls and the tail alignment is broken.
  if (calls.length < expectedCallCount) return undefined

  const source = decodePositionMarketPair({
    positionCall: calls.at(-4),
    marketCall: calls.at(-3),
    marketParams: sourceMarketParams
  })
  const target = decodePositionMarketPair({
    positionCall: calls.at(-2),
    marketCall: calls.at(-1),
    marketParams: targetMarketParams
  })

  if (!source || !target) return undefined
  return { source, target }
}
